/**
 * Hook that synthesises a live "mounted addons" view for the current tenant.
 *
 * Responsibilities:
 *   1. Bootstrap the module-local registry exactly once.
 *   2. Fetch the tenant's `addon_installations` via the SDK hook.
 *   3. Reconcile the registry's enable-set so it matches the installations.
 *   4. Call `mountExtensionPoints` and return its result.
 *
 * Non-admin users never write; this is purely a read-side reconciliation.
 */

import {
  type MountedAddons,
  mountExtensionPoints,
  unmountExtensionPoints,
} from '@sazeo/addon-runtime';
import type { AddonInstallation } from '@sazeo/schemas';
import { useAddonInstallations } from '@sazeo/sdk';
import { useEffect, useMemo } from 'react';
import { tenantSdkClient } from '../lib/sdk';
import { bootstrapAddonRegistry } from './registry';

const EMPTY_MOUNTED: MountedAddons = {
  menuEntries: [],
  dashboardWidgets: [],
  entityTabs: {},
  inlineActions: {},
  registeredClaudeToolNames: [],
};

export interface UseMountedAddonsResult {
  mounted: MountedAddons;
  installations: AddonInstallation[];
  isLoading: boolean;
  isEnabled: (addonId: string) => boolean;
}

export function useMountedAddons(tenantId: string): UseMountedAddonsResult {
  const registry = useMemo(() => bootstrapAddonRegistry(), []);
  const installationsQuery = useAddonInstallations(tenantSdkClient);
  const installations = installationsQuery.data ?? [];

  // Reconcile the registry's enabled-tenant set to match the installations
  // from the server. Runs whenever the installations list changes so disable
  // toggles propagate without a full page reload.
  useEffect(() => {
    if (!tenantId) return;
    // Unmount any previously-registered Claude tools before re-registering
    // them under the new enable-set — otherwise toggling an addon off still
    // leaves its tools live in @sazeo/claude's registry.
    unmountExtensionPoints(registry);
    for (const entry of installations) {
      if (entry.enabled) {
        try {
          registry.enableForTenant(tenantId, entry.addon_id);
        } catch {
          // Silently skip addons the registry doesn't know about — this is
          // the expected path when an installation row references an addon
          // this app version doesn't ship.
        }
      } else {
        registry.disableForTenant(tenantId, entry.addon_id);
      }
    }
  }, [tenantId, installations, registry]);

  const mounted = useMemo(() => {
    if (!tenantId) return EMPTY_MOUNTED;
    return mountExtensionPoints(registry, tenantId);
  }, [tenantId, registry]);

  const isEnabled = useMemo(() => {
    const set = new Set(installations.filter((i) => i.enabled).map((i) => i.addon_id));
    return (addonId: string) => set.has(addonId);
  }, [installations]);

  return {
    mounted,
    installations,
    isLoading: installationsQuery.isLoading,
    isEnabled,
  };
}
